import React, { useEffect, useState } from 'react';

import { useToasts } from 'react-toast-notifications';

import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import useWindowSize from '@components/common/hooks/useWindowSize';
import IconCheckmark from '@components/common/IconCheckmark/IconCheckmark';
import ConfirmModal from '@components/common/Modals/ConfirmModal/ConfirmModal';
import Pagination from '@components/common/Pagination/Pagination';
import { M_DEVICE } from '@components/lpc/Constants';
import Header from '@components/lpc/GreetingCard/Header/Header';
import Message from '@components/lpc/GreetingCard/Message/Message';
import NoMessage from '@components/lpc/GreetingCard/NoMessage/NoMessage';
import { greetingCardType, themeType } from '@propTypes/potTypes';
import greetingCardService from '@services/domain/Lpc/GreetingCardService';
import { getUserAvatarSrc } from '@services/utils/UserService';

import css from './GreetingCard.module.scss';

type GreetingCardProps = {
    title: string;
    theme: themeType;
    potRef: string;
    personRole: string;
    category: string;
};

const GreetingCard = ({ title, theme, potRef, personRole, category }: GreetingCardProps) => {
    const { t } = useTranslateLpc(['lpc-greeting-card', 'lpc-pot', 'common']);
    const { width } = useWindowSize();
    const { addToast } = useToasts();

    const [messageList, setMessageList] = useState<greetingCardType[]>([]);

    const oddList = messageList && messageList.filter((_, index) => index % 2 !== 0);
    const evenList = messageList && messageList.filter((_, index) => index % 2 === 0);

    const [isLoading, setIsLoading] = useState(true);
    const [itemPerPage] = useState<number>(999);
    const [cursor, setCursor] = useState<number>(0);
    const [pageCount, setPageCount] = useState<number>(null);
    const [pageRangeDisplayed, setPageRangeDisplayed] = useState<number>(2);

    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [selectedMessageId, setSelectedMessageId] = useState<number>(null);

    const handlePageClick = (page) => {
        setPageRangeDisplayed(2);
        const pageSelected = page.selected;
        const offset = Math.ceil(pageSelected * itemPerPage);
        setCursor(offset);
    };

    useEffect(() => {
        if (messageList.length > 0) {
            setPageCount(Math.ceil(messageList.length / itemPerPage));
        }
    }, [itemPerPage, messageList]);

    useEffect(() => {
        setIsLoading(true);
        greetingCardService
            .getAllMessages(potRef, cursor, itemPerPage)
            .then((res) => setMessageList(res))
            .catch((err) => err)
            .finally(() => setIsLoading(false));
    }, [cursor, itemPerPage, potRef]);

    const onDelete = (inviteID) => {
        const filteredList = messageList.filter((message) => message.inviteID !== inviteID);
        greetingCardService
            .deleteMessage(potRef, inviteID)
            .then((res) => {
                if (res.status === 200) {
                    setMessageList(filteredList);
                    addToast(t(`success.MESSAGE_DELETED`), {
                        appearance: 'success',
                        autoDismiss: true,
                    });
                }
            })
            .finally(() => {
                setIsDeleteModalVisible(false);
            })
            .catch((err) =>
                addToast(t(`errors.${err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            );
    };

    return (
        <div className={css.wrapper}>
            <ConfirmModal
                onHide={() => setIsDeleteModalVisible(false)}
                onConfirm={() => onDelete(selectedMessageId)}
                title={t('greeting-card.modal.delete-title')}
                text={t('greeting-card.modal.delete-text')}
                confirmLabel={t('greeting-card.modal.delete-confirm')}
                cancelLabel={t('greeting-card.modal.delete-cancel')}
                isVisible={isDeleteModalVisible}
                iconComponent={<IconCheckmark variant={'warning'} />}
                customIcon={css.modal}
            />

            <Header
                textPrimaryColor={theme.textPrimaryColor}
                title={title}
                subtitle={
                    category !== 'BEREAVEMENT' && category !== 'SOLIDARITY_POOL'
                        ? t('greeting-card.title')
                        : t('greeting-card.title-bereavement')
                }
            />

            <>
                {!isLoading && messageList.length < 1 ? (
                    <NoMessage category={category} />
                ) : (
                    <section className={css.grid} style={{ backgroundColor: theme.backgroundColorSecondary }}>
                        {messageList.length < 6 || width < M_DEVICE ? (
                            <div className={css.grid__row}>
                                {messageList?.length > 0 &&
                                    messageList.map((message) => (
                                        <Message
                                            signature={message.signature}
                                            key={message.inviteID}
                                            theme={theme}
                                            message={message.message}
                                            firstName={message.firstName}
                                            lastName={message.lastName}
                                            avatar={getUserAvatarSrc(message)}
                                            isLoading={isLoading}
                                            onDelete={() => {
                                                setIsDeleteModalVisible(true);
                                                setSelectedMessageId(message.inviteID);
                                            }}
                                            personRole={personRole}
                                        />
                                    ))}
                            </div>
                        ) : (
                            <>
                                <div className={css.grid__column}>
                                    {evenList?.length > 0 &&
                                        evenList.map((message) => (
                                            <Message
                                                signature={message.signature}
                                                key={message.inviteID}
                                                theme={theme}
                                                message={message.message}
                                                firstName={message.firstName}
                                                lastName={message.lastName}
                                                avatar={getUserAvatarSrc(message)}
                                                isLoading={isLoading}
                                                onDelete={() => onDelete(message.inviteID)}
                                                personRole={personRole}
                                            />
                                        ))}
                                </div>

                                <div className={css.grid__column}>
                                    {oddList?.length > 0 &&
                                        oddList.map((message) => (
                                            <Message
                                                signature={message.signature}
                                                key={message.inviteID}
                                                theme={theme}
                                                message={message.message}
                                                firstName={message.firstName}
                                                lastName={message.lastName}
                                                avatar={getUserAvatarSrc(message)}
                                                isLoading={isLoading}
                                                onDelete={() => onDelete(message.inviteID)}
                                                personRole={personRole}
                                            />
                                        ))}
                                </div>
                            </>
                        )}
                    </section>
                )}
            </>

            <Pagination
                handlePageClick={handlePageClick}
                pageCount={pageCount}
                pageRangeDisplayed={pageRangeDisplayed}
                marginPagesDisplayed={1}
            />
        </div>
    );
};
export default GreetingCard;
